import { useState, useCallback, useRef, useEffect } from 'react';
import ProvaRow from './ProvaRow';
import SimultaniRow from './SimultaniRow';
import { postAPI } from '../../../utils/utils';
import './EditorTable.css';

const ALL_PARTS = ['Pinya', 'Folre', 'Manilles', 'Puntals', 'Tronc', 'Organització'];

const DEFAULT_STORAGE_KEY = 'editor-proves-column-widths';

const DEFAULT_WIDTHS = {
    edit: 44,
    nom: 200,
    shortName: 100,
    subtitol: 140,
    duration: 85,
    Pinya: 110,
    Folre: 110,
    Manilles: 110,
    Puntals: 110,
    Tronc: 110,
    Organització: 100,
    castells: 300,
    modified: 110,
    actions: 44,
};

const MIN_WIDTHS = {
    nom: 120,
    shortName: 60,
    subtitol: 80,
    duration: 65,
    Pinya: 80,
    Folre: 80,
    Manilles: 80,
    Puntals: 80,
    Tronc: 80,
    Organització: 70,
    castells: 150,
    modified: 80,
};

const FIXED_COLS = new Set(['edit', 'actions']);

function loadWidths(storageKey) {
    try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const parsed = JSON.parse(saved);
            return { ...DEFAULT_WIDTHS, ...parsed };
        }
    } catch {}
    return { ...DEFAULT_WIDTHS };
}

function saveWidths(widths, storageKey) {
    try {
        const toSave = {};
        for (const key in widths) {
            if (!FIXED_COLS.has(key) && widths[key] !== DEFAULT_WIDTHS[key]) {
                toSave[key] = widths[key];
            }
        }
        localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch {}
}

function ResizeHandle({ columnKey, onResizeStart }) {
    return (
        <div
            className="resize-handle"
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onResizeStart(columnKey, e.clientX);
            }}
        />
    );
}

function SortableHeader({ field, children, sortBy, sortDirection, onSort, columnKey, onResizeStart, didResize }) {
    const isActive = sortBy === field;
    const arrow = !isActive ? '' : sortDirection === 'asc' ? ' ↑' : ' ↓';
    const isResizable = !FIXED_COLS.has(columnKey);

    const handleClick = useCallback(() => {
        if (didResize?.current) return;
        onSort(field);
    }, [onSort, field, didResize]);

    return (
        <th
            className={`sortable-header${isActive ? ' active' : ''}${isResizable ? ' resizable-th' : ''}`}
            onClick={handleClick}
        >
            {children}{arrow}
            {isResizable ? <ResizeHandle columnKey={columnKey} onResizeStart={onResizeStart} /> : null}
        </th>
    );
}

function ProvesTable({
    bundles,
    durations,
    availablePlantilles,
    socket,
    onDurationChange,
    isSimultani = false,
    visibleParts = ALL_PARTS,
    storageKey = DEFAULT_STORAGE_KEY,
    loading,
    userId,
}) {
    const [sortBy, setSortBy] = useState('shortName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [columnWidths, setColumnWidths] = useState(() => loadWidths(storageKey));
    const [isResizing, setIsResizing] = useState(false);

    const resizeRef = useRef(null);
    const tableRef = useRef(null);
    const didResizeRef = useRef(false);

    // Refs for stale closure prevention
    const bundlesRef = useRef(bundles);
    useEffect(() => { bundlesRef.current = bundles; }, [bundles]);

    // Resize handlers
    const handleResizeStart = useCallback((columnKey, startX) => {
        resizeRef.current = { columnKey, startX, startWidth: columnWidths[columnKey] || DEFAULT_WIDTHS[columnKey] };
        didResizeRef.current = false;
        setIsResizing(true);
    }, [columnWidths]);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            if (!resizeRef.current) return;
            const { columnKey, startX, startWidth } = resizeRef.current;
            const delta = e.clientX - startX;
            if (Math.abs(delta) > 2) didResizeRef.current = true;
            const minWidth = MIN_WIDTHS[columnKey] || 50;
            const newWidth = Math.max(minWidth, startWidth + delta);
            setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            setColumnWidths(prev => {
                saveWidths(prev, storageKey);
                return prev;
            });
            resizeRef.current = null;
            // Clear didResize after click event has fired
            setTimeout(() => { didResizeRef.current = false; }, 0);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const handleSort = useCallback((field) => {
        if (sortBy === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection(field === 'modified' ? 'desc' : 'asc');
        }
    }, [sortBy]);

    const handleFieldChange = useCallback((bundleId, field, value) => {
        const editData = { id: bundleId };
        editData[field] = value;
        socket.emit('.edit_bundle', editData);
    }, [socket]);

    const handleDurationChangeLocal = useCallback((bundleId, newDuration) => {
        // Optimistic update
        onDurationChange(bundleId, newDuration);

        postAPI('/duration',
            { prova: bundleId, duration: newDuration },
            () => {},
            (error) => {
                console.error('Error updating duration:', error);
            }
        );
    }, [onDurationChange]);

    const handlePartChange = useCallback((bundleId, part, plantillaName) => {
        // Changing parts requires creating a new bundle (superseding old)
        const bundle = bundlesRef.current.find(b => b.id === bundleId);
        if (!bundle) return;

        const newParts = { ...bundle.parts };
        if (plantillaName) {
            newParts[part] = plantillaName;
        } else {
            delete newParts[part];
        }

        // Mark old as hidden/superseeded and create new
        socket.emit('.edit_bundle', { id: bundleId, hidden: true, superseeded: true, hiddenBy: userId });
        socket.emit('.duplicate_bundle', bundleId, {
            nom: bundle.nom,
            shortName: bundle.shortName,
            subtitol: bundle.subtitol,
            parts: newParts,
        });
    }, [socket]);

    const handleAction = useCallback((action, bundleId) => {
        if (action === 'delete') {
            socket.emit('.edit_bundle', { id: bundleId, hidden: true, hiddenBy: userId });
        }
    }, [socket]);

    // Sort bundles
    const sortedBundles = [...bundles].sort((a, b) => {
        const dir = sortDirection === 'asc' ? 1 : -1;

        if (sortBy === 'nom') {
            return dir * (a.nom || '').localeCompare(b.nom || '');
        }
        if (sortBy === 'shortName') {
            return dir * (a.shortName || '').localeCompare(b.shortName || '');
        }
        if (sortBy === 'duration') {
            return dir * ((durations[a.id] || 0) - (durations[b.id] || 0));
        }
        if (sortBy === 'pinya') {
            return dir * (a.parts?.Pinya || '').localeCompare(b.parts?.Pinya || '');
        }
        if (sortBy === 'tronc') {
            return dir * (a.parts?.Tronc || '').localeCompare(b.parts?.Tronc || '');
        }
        if (sortBy === 'modified') {
            const dateA = a.updatedAt || a.createdAt || '';
            const dateB = b.updatedAt || b.createdAt || '';
            return dir * dateA.localeCompare(dateB);
        }
        return 0;
    });

    if (loading) {
        const skeletonCols = isSimultani ? 7 : (6 + visibleParts.length);
        return (
            <div className="editor-table-container">
                <table className={`editor-table${isSimultani ? ' editor-table-compact' : ''}`}>
                    <thead>
                        <tr>
                            <th style={{ width: 44 }}></th>
                            <th>Nom</th>
                            <th>Curt</th>
                            <th>Subtítol</th>
                            <th>Duració</th>
                            {isSimultani ? <th>Castells</th> : visibleParts.map(p => <th key={p}>{p === 'Organització' ? 'Org.' : p}</th>)}
                            <th>Modificat</th>
                            <th style={{ width: 44 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: 3 }, (_, i) => (
                            <tr key={i} className="skeleton-row">
                                {Array.from({ length: skeletonCols }, (_, j) => (
                                    <td key={j}><div className="skeleton-bar" style={{ width: '70%' }} /></td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (bundles.length === 0) {
        return (
            <div className="editor-empty-state">
                <div className="editor-empty-state-icon">&#128065;</div>
                <p className="editor-empty-state-text">No hi ha proves en aquesta secció</p>
            </div>
        );
    }

    // Build column keys for colgroup
    const colKeys = ['edit', 'nom', 'shortName', 'subtitol', 'duration'];
    if (isSimultani) {
        colKeys.push('castells');
    } else {
        visibleParts.forEach(p => colKeys.push(p));
    }
    colKeys.push('modified', 'actions');

    return (
        <div className="editor-table-container">
            <table
                ref={tableRef}
                className={`editor-table resizable-table${isSimultani ? ' editor-table-compact' : ''}${isResizing ? ' resizing-active' : ''}`}
            >
                <colgroup>
                    {colKeys.map(key => (
                        <col
                            key={key}
                            style={{ width: columnWidths[key] || DEFAULT_WIDTHS[key] }}
                        />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ width: 44 }}></th>
                        <SortableHeader field="nom" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} columnKey="nom" onResizeStart={handleResizeStart} didResize={didResizeRef}>
                            Nom
                        </SortableHeader>
                        <SortableHeader field="shortName" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} columnKey="shortName" onResizeStart={handleResizeStart} didResize={didResizeRef}>
                            Curt
                        </SortableHeader>
                        <th className="resizable-th">
                            Subtítol
                            <ResizeHandle columnKey="subtitol" onResizeStart={handleResizeStart} />
                        </th>
                        <SortableHeader field="duration" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} columnKey="duration" onResizeStart={handleResizeStart} didResize={didResizeRef}>
                            Duració
                        </SortableHeader>
                        {isSimultani ? (
                            <th className="resizable-th">
                                Castells
                                <ResizeHandle columnKey="castells" onResizeStart={handleResizeStart} />
                            </th>
                        ) : (
                            visibleParts.map(part => {
                                const isSortable = part === 'Pinya' || part === 'Tronc';
                                return isSortable ? (
                                    <SortableHeader
                                        key={part}
                                        field={part.toLowerCase()}
                                        sortBy={sortBy}
                                        sortDirection={sortDirection}
                                        onSort={handleSort}
                                        columnKey={part}
                                        onResizeStart={handleResizeStart}
                                        didResize={didResizeRef}
                                    >
                                        {part === 'Organització' ? 'Org.' : part}
                                    </SortableHeader>
                                ) : (
                                    <th key={part} className="resizable-th">
                                        {part === 'Organització' ? 'Org.' : part}
                                        <ResizeHandle columnKey={part} onResizeStart={handleResizeStart} />
                                    </th>
                                );
                            })
                        )}
                        <SortableHeader field="modified" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} columnKey="modified" onResizeStart={handleResizeStart} didResize={didResizeRef}>
                            Modificat
                        </SortableHeader>
                        <th style={{ width: 44 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {sortedBundles.map(bundle => (
                        isSimultani ? (
                            <SimultaniRow
                                key={bundle.id}
                                bundle={bundle}
                                duration={durations[bundle.id]}
                                onFieldChange={handleFieldChange}
                                onDurationChange={handleDurationChangeLocal}
                                onAction={handleAction}
                                sortBy={sortBy}
                            />
                        ) : (
                            <ProvaRow
                                key={bundle.id}
                                bundle={bundle}
                                duration={durations[bundle.id]}
                                availablePlantilles={availablePlantilles}
                                visibleParts={visibleParts}
                                onFieldChange={handleFieldChange}
                                onDurationChange={handleDurationChangeLocal}
                                onPartChange={handlePartChange}
                                onAction={handleAction}
                                sortBy={sortBy}
                            />
                        )
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default ProvesTable;
